import { IconCreditCardPlusFill18 } from "nucleo-ui-fill-18"

import { TRIAL_AI_RESOLUTIONS } from "@productlane/lib/src/plans-client"
import { Button, Input } from "@productlane/ui"

import { toast } from "@/components/toast"
import { SettingItem } from "../settings-switches/setting-item"
import { AI_LIMIT_FIELD_ID } from "./ai-limit-focus"

interface AiOverageLimitFieldProps {
	trialing: boolean
	// True once spend passed the limit, so the copy stops promising a future pause.
	reached?: boolean
	enabled: boolean
	value: string
	open: boolean
	onOpenChange: (open: boolean) => void
	onEnabledChange: (enabled: boolean) => void
	onValueChange: (value: string) => void
	onCommit: () => void
	onAddPaymentMethod: () => void
}

export function AiOverageLimitField({
	trialing,
	reached = false,
	enabled,
	value,
	open,
	onOpenChange,
	onEnabledChange,
	onValueChange,
	onCommit,
	onAddPaymentMethod,
}: AiOverageLimitFieldProps) {
	const explainTrialBlock = () => {
		toast.info({
			id: "ai-overage-trial-blocked",
			title: "Add a card to set an overage limit",
			description: `Your trial includes ${TRIAL_AI_RESOLUTIONS} AI resolutions, then the agent pauses.`,
			icon: IconCreditCardPlusFill18,
			action: {
				label: "Add payment details",
				callback: onAddPaymentMethod,
			},
		})
	}

	const showEditor = open && enabled && !trialing

	return (
		// eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
		<div
			id={AI_LIMIT_FIELD_ID}
			className="flex flex-col gap-3 pt-4"
			onClick={trialing ? explainTrialBlock : undefined}
			data-testid="ai-overage-limit-field"
		>
			<SettingItem
				title="Monthly overage limit"
				description={
					trialing
						? "Overage needs a payment method on file."
						: reached
							? "Spend reached this limit, so AI is paused. Raise it to start the agent again."
							: enabled
								? `AI pauses once overage reaches $${value || "0"}.`
								: "Overage runs without a limit, so AI keeps going past your credits."
				}
				action={
					<Button
						variant="outline"
						disabled={trialing}
						className={trialing ? "pointer-events-none" : undefined}
						onClick={() => {
							if (trialing) return
							if (!enabled) {
								onEnabledChange(true)
								onOpenChange(true)
								return
							}
							onOpenChange(!open)
						}}
					>
						{enabled ? "Adjust limit" : "Set a limit"}
					</Button>
				}
			/>
			{showEditor && (
				<div className="flex items-center gap-2.5">
					<Input
						name="cap"
						value={value}
						onChange={(e) => onValueChange(e.target.value)}
						onBlur={onCommit}
						placeholder="0"
						type="number"
						className="w-28"
					/>
					<span className="text-xs text-tertiary">
						extra dollars a month, past your credits
					</span>
					<Button
						variant="ghost"
						size="sm"
						className="ml-auto"
						onClick={() => {
							onEnabledChange(false)
							onOpenChange(false)
						}}
					>
						Remove limit
					</Button>
				</div>
			)}
		</div>
	)
}
